<template>
  <section class="">
    <div v-if="posts.length > limit">
      <a href="#" @click.prevent="isLimit = !isLimit">
        <span v-if="isLimit">Alle anzeigen</span>
        <span v-else>Nur neue anzeigen</span>
      </a>
    </div>
    <ul class="post-comments mt-sm">
      <li
        v-for="post in limitedPosts"
        :key="post.id"
        :class="{ 'current-user': isCurrentUser(post) }"
      >
        <span v-if="!isCurrentUser(post)" class="thumb-xs avatar pull-left mr-sm">
          <img
            v-if="post.user.avatar"
            class="rounded-circle"
            :src="post.user.avatar.path"
            :style="{ borderColor: getUserColor(post.user) }"
            alt=""
          />
          <div v-else class="avatar-initials" :style="{ backgroundColor: getUserColor(post.user) }">
            {{ getUserInitials(post.user) }}
          </div>
        </span>
        <div
          class="comment-body"
          @mouseover="enablePostOptions(post.id)"
          @mouseleave="disablePostOptions(post.id)"
        >
          <div class="speech-bubble">
            <div class="comment-options" v-if="activeId == post.id && post.user.id == user.id">
              <span v-if="editable" class="icon" @click.prevent="editPost(post.id)"
                ><i class="fa fa-edit"></i
              ></span>
              <span v-if="deleteable" class="icon" @click.prevent="deletePost(post.id)"
                ><i class="fa fa-times"></i
              ></span>
            </div>
            <h6 class="author fs-sm fw-semi-bold" :style="{ color: getUserColor(post.user) }">
              {{ post.user.firstname }} {{ post.user.lastname }}
              <small>
                {{ $fromNow(post.createdAt) }}
                {{ $datetime(post.createdAt) }}
                <b-badge v-if="isNewCustomerPost(post)" class="ml-2" variant="yellow">{{
                  $t(post.state)
                }}</b-badge>
              </small>
            </h6>
            <p>{{ post.content }}</p>
          </div>
        </div>
      </li>
      <li>
        <span class="thumb-xs avatar pull-left mr-sm">
          <img v-if="user.avatar" class="rounded-circle" :src="user.avatar.path" alt="" />
          <div v-else class="avatar-initials" :style="{ backgroundColor: getUserColor(user) }">
            {{ getUserInitials(user) }}
          </div>
        </span>
        <form v-on:submit.prevent="savePost" class="comment-form">
          <div class="comment-body">
            <b-form-input
              v-model="post"
              class="form-control form-control-sm"
              type="text"
              :placeholder="$t('write_a_post')"
            />
            <button type="submit" class="btn btn-secondary btn-sm">{{ $t('send') }}</button>
          </div>
        </form>
      </li>
    </ul>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Button from 'primevue/button';

/**
 * @todo: Loader Icon
 * @todo: fetch only recent posts, max number
 * @todo: fetch more/all link
 * @todo: delete own posts
 * @todo: edit own posts
 */
export default {
  name: 'Post',
  props: {
    user: null,
    editable: { type: Boolean, default: false },
    deleteable: { type: Boolean, default: false },
    posts: {
      type: Array,
      required: true,
    },
    components: {
      Button,
    },
  },
  data() {
    return {
      post: null,
      activeId: null,
      limit: 10,
      isLimit: true,
      userColors: [
        '#FF6B6B', // coral red
        '#02a698', // teal
        '#98269f', // purple
        '#b4876e', // brown
        '#e542a3', // pink
        '#e8c547', // yellow
        '#4ECDC4', // turquoise
        '#fa6533', // orange
        '#1daa61', // green
        '#ffb938', // yellow
        '#7d9ef1', // blue
        '#c58730', // brown
        '#45B7D1', // sky blue
        '#FFA07A', // light salmon
      ],
      userColorMap: {}, // Map of user.id to color
      currentUserColor: '#52C234',
    };
  },
  computed: {
    /**
     * apply post limit, return the last [limit] posts from the posts array if limit is set
     */
    limitedPosts() {
      // Keep newest posts, cut oldest when limit is applied
      return this.isLimit ? this.posts.slice(-this.limit) : this.posts;
    },
  },
  watch: {
    /**
     * Reassign colors when posts change
     */
    posts: {
      handler() {
        this.assignUserColors();
      },
      deep: true,
    },
  },
  methods: {
    ...mapGetters('auth', ['getCurrentUser']),
    ...mapGetters([]),
    savePost() {
      if (!this.post || this.post.trim() === '') {
        return;
      }
      this.$emit('comment:post', this.post);
      this.post = null;
    },
    deletePost(id) {
      this.$emit('comment:delete', id);
      // console.log("delete:" + id)
    },
    editPost(id) {
      // console.log("edit:" + id)
    },
    enablePostOptions(id) {
      this.activeId = id;
    },
    disablePostOptions(id) {
      this.activeId = null;
    },
    isNewCustomerPost(post) {
      return post.state === 'POST_NEW';
    },
    getUserColor(user) {
      // Current user gets fixed color
      if (user.id === this.user.id) {
        return this.currentUserColor;
      }
      // Other users get colors from the rotating array
      return this.userColorMap[user.id] || this.userColors[0];
    },
    getUserInitials(user) {
      const firstInitial = user.firstname ? user.firstname.charAt(0).toUpperCase() : '';
      const lastInitial = user.lastname ? user.lastname.charAt(0).toUpperCase() : '';
      return firstInitial + lastInitial;
    },
    assignUserColors() {
      const userIds = new Set();
      let colorIndex = 0;

      this.posts.forEach((post) => {
        if (!userIds.has(post.user.id)) {
          userIds.add(post.user.id);
          // Skip current user - they get fixed color
          if (post.user.id !== this.user.id) {
            this.userColorMap[post.user.id] = this.userColors[colorIndex];
            colorIndex++;
          }
        }
      });
    },
    isCurrentUser(post) {
      return post.user.id === this.user.id;
    },
  },
  async mounted() {
    this.assignUserColors();
  },
};
</script>

<style lang="scss" scoped>
.post-comments {
  margin-bottom: calc(calc($spacer / 2));
  font-size: $font-size-sm;

  @include clearfix();

  > li {
    padding: 0.5em 0.1em;
    margin-bottom: 0.5em;
    list-style: none;

    @include clearfix();
  }

  p:last-child {
    margin-bottom: 0;
  }

  .avatar {
    margin-top: 1px;
  }

  .author {
    margin-top: 0;
    margin-bottom: 2px;
  }

  .comment-body {
    overflow: auto;
  }

  h6.author > small {
    font-size: 10px;
    color: #9a9a9a;
    font-weight: 600;
  }

  .widget > footer & {
    margin-left: -$widget-padding-horizontal;
    margin-right: -$widget-padding-horizontal;
  }
}

.avatar img {
  width: 28px;
  height: 28px;
  object-fit: cover;
  border: 2px solid transparent;
}

.avatar-icon {
  font-size: 28px;
}

.avatar-initials {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  padding-top: 2px;
}

/* Speech bubble styling */
.speech-bubble {
  background-color: #f1f2f3;
  border-radius: 2px;
  padding: 6px 10px;
  margin-left: 8px;
  margin-right: 40px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  max-width: 85%;
}

.speech-bubble::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 12px;
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #f1f2f3;
}

/* Current user styling */
.current-user .speech-bubble {
  background-color: #e5f0e8;
  margin-left: auto;
  margin-right: 8px;
  max-width: 85%;
}

.current-user .speech-bubble::before {
  left: auto;
  right: -8px;
  border-right: none;
  border-left: 8px solid #e5f0e8;
}
.event {
  background: none;
}

ul.post-comments {
  list-style-type: none;
  padding-left: 0;
}

.post-comments li.new-post {
  transition: background-color 2s ease;
  background-color: rgba(40, 167, 49, 0.4);
}

.post-comments li {
  margin-bottom: 0;
}

.comment-body {
  position: relative;
}

.comment-options {
  position: absolute;
  top: -2px;
  right: 8px;
  background-color: transparent;
  color: #bbb;
  padding: 0 0 0 10px;
  border: 0px solid #bbbbbb;
}

.comment-options .icon {
  margin: 0 2px;
  cursor: pointer;
}

.comment-options .icon i.fa-edit {
  position: relative;
  top: 1px;
}

.comment-options .icon .fa-edit:hover {
  color: $info;
}

.comment-options .icon .fa-times:hover {
  color: $danger;
}

.comment-body .form-control {
  flex-grow: 1;
  margin-right: 10px;
}

.comment-body button {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

.comment-form .comment-body {
  display: flex; /* Ändert das Display auf Flex, damit die Eingabe und der Button horizontal angeordnet werden */
  align-items: center; /* Zentriert die Inhalte vertikal */
}

.author {
  margin-bottom: 0; /* Reduziert den Abstand unterhalb des Autors */
}

.comment-body p {
  margin: 0; /* Entfernt den Standardabstand des Absatzes */
  padding-top: 3px; /* Fügt einen kleinen Abstand über der Nachricht hinzu */
}

/* Color Legend Styling */
.color-legend {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e7e7e7;
}

.legend-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.legend-colors {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.legend-color-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
}
</style>
